import { Helmet } from "react-helmet-async";

interface PropertyStructuredDataProps {
  name: string;
  description: string;
  image: string;
  price: string;
  address: string;
  neighborhood: string;
  beds: number;
  baths: number;
  sqft: string;
  propertyType: string;
  yearBuilt?: number;
}

export const PropertyStructuredData = ({
  name,
  description,
  image,
  price,
  address,
  neighborhood,
  beds,
  baths,
  sqft,
  propertyType,
  yearBuilt,
}: PropertyStructuredDataProps) => {
  // Parse price to get numeric value
  const numericPrice = price.replace(/[^0-9]/g, "");
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name,
    description,
    image,
    url: typeof window !== "undefined" ? window.location.href : "",
    offers: {
      "@type": "Offer",
      price: numericPrice,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: address,
      addressLocality: neighborhood,
      addressRegion: "IL",
      addressCountry: "US",
    },
    numberOfRooms: beds + baths,
    numberOfBedrooms: beds,
    numberOfBathroomsTotal: baths,
    floorSize: {
      "@type": "QuantitativeValue",
      value: sqft.replace(/[^0-9]/g, ""),
      unitCode: "SQF",
    },
    propertyType,
    ...(yearBuilt && { yearBuilt }),
    broker: {
      "@type": "RealEstateAgent",
      name: "Brant Booker",
      url: "https://brantbooker.com",
      telephone: "(312) 555-1234",
      email: "brant@brantbooker.com",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

interface ArticleStructuredDataProps {
  title: string;
  description: string;
  image?: string;
  authorName?: string;
  authorUrl?: string;
  publishedAt: string;
  modifiedAt?: string;
  category: string;
}

export const ArticleStructuredData = ({
  title,
  description,
  image,
  authorName = "Brant Booker",
  authorUrl,
  publishedAt,
  modifiedAt,
  category,
}: ArticleStructuredDataProps) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    ...(image && { image }),
    url: typeof window !== "undefined" ? window.location.href : "",
    datePublished: publishedAt,
    dateModified: modifiedAt || publishedAt,
    author: {
      "@type": "Person",
      name: authorName,
      ...(authorUrl && { url: authorUrl }),
    },
    publisher: {
      "@type": "Organization",
      name: "Brant Booker Real Estate",
      url: "https://brantbooker.com",
      logo: {
        "@type": "ImageObject",
        url: "https://brantbooker.com/og-image.png",
      },
    },
    articleSection: category,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": typeof window !== "undefined" ? window.location.href : "",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

interface OrganizationStructuredDataProps {
  name?: string;
  url?: string;
  logo?: string;
  phone?: string;
  email?: string;
}

const SITE_URL = "https://brantbooker.com";

export const OrganizationStructuredData = ({
  name = "Brant Booker Real Estate",
  url = SITE_URL,
  logo = `${SITE_URL}/og-image.png`,
  phone = "(312) 555-1234",
  email = "brant.booker@sothebysrealty.com",
}: OrganizationStructuredDataProps = {}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "@id": `${SITE_URL}/#organization`,
    name,
    url,
    logo,
    telephone: phone,
    email,
    description: "Brant Booker is a top Chicago real estate agent with Jameson Sotheby's International Realty, specializing in luxury homes with over $900M in sales and 1,900+ transactions.",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Chicago",
      addressRegion: "IL",
      postalCode: "60614",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.8781,
      longitude: -87.6298,
    },
    areaServed: [
      { "@type": "City", name: "Chicago" },
      { "@type": "Neighborhood", name: "Gold Coast" },
      { "@type": "Neighborhood", name: "Lincoln Park" },
      { "@type": "Neighborhood", name: "River North" },
      { "@type": "Neighborhood", name: "Lakeview" },
      { "@type": "Neighborhood", name: "Wicker Park" },
    ],
    sameAs: [
      "https://www.linkedin.com/in/brant-booker-098b4520",
    ],
    knowsAbout: [
      "Chicago luxury real estate",
      "Residential property sales",
      "Investment properties",
      "New construction homes",
      "First-time home buying",
      "Real estate market analysis",
      "Property negotiation",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Real Estate Services",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Property Search" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Luxury Home Sales" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "First-Time Buyer Guidance" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Investment Properties" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Market Analysis" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Negotiation Services" } },
      ],
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbStructuredDataProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbStructuredData = ({ items }: BreadcrumbStructuredDataProps) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQStructuredDataProps {
  faqs: FAQItem[];
}

export const FAQStructuredData = ({ faqs }: FAQStructuredDataProps) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

interface ServiceStructuredDataProps {
  name: string;
  description: string;
  provider?: string;
  areaServed?: string;
  url: string;
}

export const ServiceStructuredData = ({
  name,
  description,
  provider = "Brant Booker",
  areaServed = "Chicago, IL",
  url,
}: ServiceStructuredDataProps) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    provider: {
      "@type": "RealEstateAgent",
      name: provider,
      url: SITE_URL,
    },
    areaServed: {
      "@type": "City",
      name: areaServed,
    },
    url,
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

interface LocalBusinessStructuredDataProps {
  neighborhood: string;
  description: string;
}

export const LocalBusinessStructuredData = ({
  neighborhood,
  description,
}: LocalBusinessStructuredDataProps) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/neighborhoods/${neighborhood.toLowerCase().replace(/\s+/g, "-")}`,
    name: `Brant Booker - ${neighborhood} Real Estate`,
    description,
    url: `${SITE_URL}/neighborhoods/${neighborhood.toLowerCase().replace(/\s+/g, "-")}`,
    areaServed: {
      "@type": "Neighborhood",
      name: neighborhood,
      containedInPlace: {
        "@type": "City",
        name: "Chicago",
        containedInPlace: {
          "@type": "State",
          name: "Illinois",
        },
      },
    },
    parentOrganization: {
      "@type": "RealEstateAgent",
      name: "Brant Booker Real Estate",
      url: SITE_URL,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export const WebSiteStructuredData = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: "Brant Booker Chicago Real Estate",
    url: SITE_URL,
    description: "Find luxury homes and expert real estate guidance in Chicago with Brant Booker of Jameson Sotheby's International Realty.",
    publisher: {
      "@type": "RealEstateAgent",
      "@id": `${SITE_URL}/#organization`,
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/properties?search={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export const PersonStructuredData = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE_URL}/#person`,
    name: "Brant Booker",
    jobTitle: "Real Estate Agent",
    worksFor: {
      "@type": "Organization",
      name: "Jameson Sotheby's International Realty",
    },
    url: SITE_URL,
    email: "brant.booker@sothebysrealty.com",
    sameAs: [
      "https://www.linkedin.com/in/brant-booker-098b4520",
    ],
    knowsAbout: [
      "Chicago real estate",
      "Luxury homes",
      "Investment properties",
      "New construction",
      "First-time home buying",
    ],
    description: "Brant Booker has sold over $900 million in Chicago real estate with 1,900+ transactions spanning 20+ years. Specializing in luxury homes, new construction, and investment properties.",
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};